import { useEffect } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import { MarketplacePropertyCategory_AccountCreatedEvent_AuthMethod as AccountCreatedAuthMethod } from '@opendoor/observability/pbs/journey/data/categories/marketplace_property_category_pb';
import Cookies from 'js-cookie';

import { useObservability } from '../../helpers/observability';

// Written by web's Humans::LoginConcerns#set_pending_client_registration_cookie
// when a Google/Apple callback creates an account; the name and each page's
// path prefix must match PENDING_CLIENT_REGISTRATION_COOKIE_PATHS there.
export const PENDING_CLIENT_REGISTRATION_COOKIE = 'od_pending_client_registration';

const AUTH_METHOD_BY_PROVIDER = new Map<string, AccountCreatedAuthMethod>([
  ['google', AccountCreatedAuthMethod.GOOGLE],
  ['apple', AccountCreatedAuthMethod.APPLE],
]);

type PendingOAuthAccountCreatedEventOptions = {
  /** null when the page has no trustworthy address: the marker is still consumed, nothing fires. */
  addressUuid: string | null;
  /** Public URL prefix the cookie was scoped to, needed to delete it. */
  cookiePath: '/properties' | '/checkout';
};

/**
 * OAuth sign-up registers inside a full-page redirect, so the accountCreated
 * event that AccountFormPage fires on email registration has no equivalent
 * moment. The callback leaves a one-shot cookie instead, and the page the
 * redirect returns to fires the event once auth resolves to the new human.
 * Any authenticated visit consumes the marker, even one that won't report, so
 * a leftover can't fire later for an unrelated property or person.
 */
export function usePendingOAuthAccountCreatedEvent({
  addressUuid,
  cookiePath,
}: PendingOAuthAccountCreatedEventOptions): void {
  const { authentication } = useAuth();
  const { trackJourneyEvent } = useObservability();
  const authenticated = authentication.state === 'authenticated';
  const impersonated = authenticated && authentication.user.impersonated;

  useEffect(() => {
    if (!authenticated) {
      return;
    }

    const provider = Cookies.get(PENDING_CLIENT_REGISTRATION_COOKIE);
    if (provider === undefined) {
      return;
    }
    Cookies.remove(PENDING_CLIENT_REGISTRATION_COOKIE, { path: cookiePath });

    const authMethod = AUTH_METHOD_BY_PROVIDER.get(provider);
    if (impersonated || addressUuid === null || authMethod === undefined) {
      return;
    }

    trackJourneyEvent(
      'marketplaceProperty',
      'accountCreated',
      { addressUuid, authMethod },
      { flush: true },
    );
  }, [addressUuid, authenticated, cookiePath, impersonated, trackJourneyEvent]);
}
